declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

export function pixelTrack(event: string, params?: Record<string, unknown>, eventID?: string) {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      // When eventID is passed, FB dedupes this client-side event against any
      // server-side CAPI event that arrives with the same event_id. Without
      // it, FB counts them as 2 separate conversions — the most common cause
      // of inflated Events Manager numbers.
      if (eventID) {
        window.fbq('track', event, params, { eventID });
      } else {
        window.fbq('track', event, params);
      }
      console.log('[FB Pixel]', event, 'fired', eventID ? `(eventID: ${eventID})` : '');
    }
  } catch {}
}

export function pixelPageView() {
  pixelTrack('PageView');
}

export function pixelRegistration() {
  pixelTrack('CompleteRegistration', {
    content_name: 'Papacito Signup',
    currency: 'USD',
    value: 0,
    status: true,
  });
}

export function pixelLead() {
  pixelTrack('Lead', {
    content_name: 'Papacito Onboarding',
    content_category: 'Dating App',
  });
}

export function pixelInitiateCheckout() {
  pixelTrack('InitiateCheckout', {
    content_name: 'Papacito Premium',
    currency: 'USD',
  });
}

export function pixelSearch(searchTerm?: string) {
  pixelTrack('Search', {
    search_string: searchTerm || 'discover',
  });
}

export function pixelViewContent(contentName: string) {
  pixelTrack('ViewContent', {
    content_name: contentName,
    content_type: 'dating_profile',
  });
}

export function pixelContact() {
  pixelTrack('Contact', {
    content_name: 'Match Message Sent',
  });
}

export function pixelPurchase(value = 4.95) {
  pixelTrack('Purchase', {
    currency: 'USD',
    value,
    content_name: 'Papacito VIP',
    content_type: 'product',
  });
}

export function pixelCustom(eventName: string, params?: Record<string, unknown>, eventID?: string) {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      if (eventID) {
        window.fbq('trackCustom', eventName, params, { eventID });
      } else {
        window.fbq('trackCustom', eventName, params);
      }
      console.log('[FB Pixel]', eventName, 'fired', eventID ? `(eventID: ${eventID})` : '');
    }
  } catch {}
}

export function fireKeitaroPostback() {
  const clickId = sessionStorage.getItem('keitaro_clickid');
  if (!clickId) return;

  try {
    fetch(
      `https://geniusfluence.com/postback?clickid=${encodeURIComponent(clickId)}&status=1&payout=0`,
      { method: 'GET', mode: 'no-cors' }
    ).catch(() => {});
  } catch {}
}

export function getKeitaroSubid(): string | null {
  const cookieMatch = document.cookie.match(/keitaro_subid=([^;]+)/);
  if (cookieMatch) return decodeURIComponent(cookieMatch[1]);
  return localStorage.getItem('keitaro_subid');
}

/**
 * Returns the full tracking bundle for ad attribution, click-id passthrough,
 * and downstream Whop stitching.
 *
 * Read order (most durable → least):
 *   1. localStorage `papacito_tracking` (written by /install lander AND by
 *      the inline capture in index.html). Survives PWA standalone-mode
 *      launches where sessionStorage is empty.
 *   2. sessionStorage (legacy keys written by index.html capture).
 *
 * Why localStorage first: PWA "Add to Home Screen" → standalone launch
 * starts in a fresh browsing context; only localStorage + cookies persist.
 * If we read sessionStorage first we'd get nulls and miss attribution on
 * exactly the most valuable conversion (installed users).
 *
 * `referrer` is `document.referrer` captured at first lander load, NOT
 * the live `document.referrer` (which is empty on direct PWA launches).
 */
export function getTrackingData() {
  // localStorage bundle from /install or index.html capture.
  let bundle: Record<string, unknown> = {};
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('papacito_tracking') : null;
    if (raw) bundle = JSON.parse(raw) || {};
  } catch {
    // localStorage may throw in private mode; fall back to sessionStorage.
  }
  const pick = (key: string): string | null => {
    const fromBundle = bundle[key];
    if (typeof fromBundle === 'string' && fromBundle) return fromBundle;
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };
  return {
    utmSource:      pick('utm_source'),
    utmMedium:      pick('utm_medium'),
    utmCampaign:    pick('utm_campaign'),
    utmContent:     pick('utm_content'),
    utmTerm:        pick('utm_term'),
    fbclid:         pick('fbclid'),
    gclid:          pick('gclid'),
    keitaroClickId: pick('keitaro_clickid') || (typeof bundle.subid === 'string' ? (bundle.subid as string) : null),
    keitaroSubid:   getKeitaroSubid() || (typeof bundle.subid === 'string' ? (bundle.subid as string) : null),
    referrer:       pick('referrer'),
  };
}
